// API Service Layer for Vulcan View Dashboard - Real Mining-Dutch.nl APIs

import { DashboardStats, MinerStatus } from '../types';

// Direct API configuration pointing to backend
import { API_CONFIG } from '../config/api';

const PROXY_CONFIG = {
  BASE_URL: API_CONFIG.baseURL, // Use backend URL directly
  POOLS: {
    DOGECOIN: 'dogecoin',
    SHA256: 'sha256'
  }
};

// Response interfaces matching Android models
interface UserWorkersResponse {
  getuserworkers: {
    version?: string;
    runtime?: number;
    data?: {
      count?: {
        active?: number;
        inactive?: number;
      };
      miners?: WorkerData[];
    };
  };
}

interface WorkerData {
  username?: string;
  minerGroup?: string;
  mode?: string;
  alive?: number;
  hashrate?: number;
  hashrateShared?: string;
  lastshare?: number;
  shares?: string;
  difficulty?: string;
  nowMining?: string;
  mergedMining?: number;
}

interface UserBalanceResponse {
  getuserbalance?: {
    version?: string;
    runtime?: number;
    data?: {
      confirmed?: string;
      unconfirmed?: string;
      orphaned?: string;
    };
  };
}

// Proxy API client to bypass CORS
class ProxyApiClient {
  private baseURL: string;

  constructor() {
    this.baseURL = PROXY_CONFIG.BASE_URL;
  }

  private async request<T>(endpoint: string, retries = 2): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    console.log(`🔄 Backend API Request: ${url} (${3 - retries}/3 attempts)`);
    
    try {
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      console.log(`📡 Backend Response Status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle circuit breaker responses
        if (response.status === 503 && errorData.circuitBreakerState === 'OPEN') {
          const retryAfter = errorData.retryAfter || 30;
          console.log(`🚨 Circuit breaker is OPEN. Retry after ${retryAfter}s`);
          throw new Error(`Service temporarily unavailable. Circuit breaker is open. Retry in ${retryAfter}s`);
        }
        
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`✅ Backend Response:`, data);
      return data;
    } catch (error) {
      console.error(`❌ Backend request failed for ${endpoint}:`, error);
      
      // Handle AbortError (timeout)
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout after 15 seconds');
      }
      
      // Retry logic for network errors (but not for circuit breaker)
      if (retries > 0 && error instanceof Error && !error.message.includes('Circuit breaker')) {
        console.log(`🔄 Retrying request in 2s... (${retries} attempts left)`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return this.request<T>(endpoint, retries - 1);
      }
      
      throw error;
    }
  }

  // Get user workers for a specific pool from backend
  async getUserWorkers(pool: string): Promise<UserWorkersResponse> {
    console.log(`🔍 Fetching workers for pool: ${pool} from backend`);
    return this.request<UserWorkersResponse>(`/api/workers/${pool}`);
  }

  // Get user balance for DOGE from backend
  async getDogeBalance(): Promise<UserBalanceResponse> {
    console.log(`💰 Fetching DOGE balance from backend`);
    return this.request<UserBalanceResponse>('/api/balance/doge');
  }



  // Scan single IP for miner configuration through backend
  async scanMinerIP(url: string, username: string, password: string): Promise<any> {
    console.log(`🔍 Scanning miner IP through backend: ${url}`);
    
    const response = await fetch(`${this.baseURL}/api/scanner`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        url,
        username,
        password
      })
    });

    if (!response.ok) {
      throw new Error(`Scanner request failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }
}

const dedupeMiners = (miners: MinerStatus[]) => {
  const seen = new Map<string, MinerStatus>();

  for (const miner of miners) {
    const key = miner.name; // tu username normalizado

    if (!seen.has(key)) {
      seen.set(key, { ...miner });
    } else {
      const existing = seen.get(key)!;

      // Unir hashrates de duplicados (LTC+DOGE o LTC+SHA)
      existing.hashrate += miner.hashrate;

      // Online > offline
      if (existing.status !== 'online' && miner.status === 'online') {
        existing.status = 'online';
      }
    }
  }

  return Array.from(seen.values());
};

// Create singleton instance
export const apiClient = new ProxyApiClient();

// Mining services using real APIs
export const miningServices = {
  // Get all workers from all pools
  getAllWorkers: async (): Promise<MinerStatus[]> => {
    console.log(`🔄 Fetching all workers from all pools...`);
    
    try {
      // Fetch workers from both pools
      const [ sha256Response, dogecoinResponse] = await Promise.all([
        apiClient.getUserWorkers(PROXY_CONFIG.POOLS.SHA256),
        apiClient.getUserWorkers(PROXY_CONFIG.POOLS.DOGECOIN)
      ]);

      const sha256Miners = sha256Response.getuserworkers?.data?.miners || [];
      const dogecoinMiners = dogecoinResponse.getuserworkers?.data?.miners || [];
 
      
      console.log(`📊 Found ${sha256Miners.length} SHA256 miners, ${dogecoinMiners.length}, Dogecoins miners`);

      // Convert to MinerStatus format
      const allMiners: MinerStatus[] = [];
      

      // Process SHA256 miners (hashrate comes in MH/s, convert to GH/s for display)
      sha256Miners.forEach((miner, index) => {
        allMiners.push({
          id: `sha256_${index}`,
          name: miner.username || `SHA256-${index}`,
          status: (miner.alive === 1 && (miner.hashrate || 0) > 0) ? 'online' : 
                  (miner.alive === 1) ? 'error' : 'offline',
          hashrate: (miner.hashrate || 0), // Keep in MH/s for proper GH/s conversion
          difficulty: parseFloat(miner.difficulty as string || '0'),
          algorithm: 'SHA256',
          pool: 'SHA256 Pool',
          nowMining: miner.nowMining || 'Unknown',
          lastSeen: Date.now() - ((miner.lastshare || 0) * 1000),
          temperature: undefined, // Not available from API
          power: undefined // Not available from API
        });
      });
      // Process Dogecoin miners
      dogecoinMiners.forEach((miner, index) => {
        allMiners.push({
          id: `doge_${index}`,
          name: miner.username || `Dogecoin-${index}`,
          status: (miner.alive === 1 && (miner.hashrate || 0) > 0) ? 'online' : 
                  (miner.alive === 1) ? 'error' : 'offline',
          hashrate: (miner.hashrate || 0), // Keep in MH/s for proper formatting
          difficulty:  parseFloat(miner.difficulty as string || '0'),
          algorithm: 'Scrypt',
          pool: 'Dogecoin Pool',
          nowMining:miner.nowMining || 'Unknown',
          lastSeen: Date.now() - ((miner.lastshare || 0) * 1000),
          temperature: undefined, // Not available from API
          power: undefined // Not available from API
        });
      });


      console.log(`✅ Processed ${allMiners.length} total miners`);

      const finalList = dedupeMiners(allMiners);
      console.log(`🧹 Cleaned duplicates: ${allMiners.length} → ${finalList.length}`);
      return finalList;

    } catch (error) {
      console.error('❌ Failed to fetch all workers:', error);
      return [];
    }
  },

  // Get dashboard statistics
  getDashboardStats: async (): Promise<DashboardStats> => {
    console.log(`📊 Calculating dashboard statistics...`);
    
    try {
      const miners = await miningServices.getAllWorkers();
      
      const onlineMiners = miners.filter(m => m.status === 'online');
      const totalHashrate = onlineMiners.reduce((sum, miner) => sum + miner.hashrate, 0);
      const algorithms = new Set(miners.map(m => m.algorithm));
      
      const stats: DashboardStats = {
        totalPools: 2, // Dogecoin + SHA256
        totalWorkers: miners.length, // Exclude Dogecoin miners
        totalHashrate,
        activeAlgorithms: algorithms.size,
        topProfitableAlgorithm: algorithms.size > 0 ? Array.from(algorithms)[0] : '---'
      };

      console.log(`✅ Dashboard stats calculated:`, stats);
      return stats;

    } catch (error) {
      console.error('❌ Failed to calculate dashboard stats:', error);
      return {
        totalPools: 0,
        totalWorkers: 0,
        totalHashrate: 0,
        activeAlgorithms: 0,
        topProfitableAlgorithm: '---'
      };
    }
  },

  // Get crypto progress data for status panel (only DOGE)
  getCryptoProgress: async () => {
    console.log(`💎 Fetching crypto progress data...`);
    
    try {
      const dogeBalance = await apiClient.getDogeBalance();

      const dogeConfirmed = parseFloat(dogeBalance.getuserbalance?.data?.confirmed || '0');

      console.log(`💰 DOGE Balance: ${dogeConfirmed}`);

      // Calculate progress towards goals (matching Android app)
      const dogeGoal = 5000; // 5,000 DOGE goal

      const dogeProgress = Math.min((dogeConfirmed / dogeGoal) * 100, 100);

      return {
        doge: {
          name: 'Dogecoin',
          symbol: 'DOGE',
          progress: dogeProgress,
          hashrate: 0, // Not available from balance API
          profitability: '---', // Not available from balance API
          balance: dogeConfirmed,
          goal: dogeGoal
        }
      };

    } catch (error) {
      console.error('❌ Failed to get crypto progress:', error);
      return { doge: null };
    }
  },

  // Get average hashrate for both pools with correct units
  getAverageHashrate: async () => {
    console.log(`📈 Calculating average hashrate for both pools...`);
    
    try {
      // Get workers from both pools
      const [ sha256Response, dogecoinResponse] = await Promise.all([
        apiClient.getUserWorkers(PROXY_CONFIG.POOLS.DOGECOIN),
        apiClient.getUserWorkers(PROXY_CONFIG.POOLS.SHA256)
      ]);

      const dogecoinMiners = dogecoinResponse.getuserworkers?.data?.miners || [];
      const sha256Miners = sha256Response.getuserworkers?.data?.miners || [];
      
      // Filter only online miners
      const onlineSha256Miners = sha256Miners.filter(miner => miner.alive === 1 && (miner.hashrate || 0) > 0);
      const onlinedDogecoinMiners = dogecoinMiners.filter(miner => miner.alive === 1 && (miner.hashrate || 0) > 0);
      
      // Calculate SHA256 hashrate (comes in MH/s, keep as MH/s for now)
      const sha256TotalMH = onlineSha256Miners.reduce((sum, miner) => sum + (miner.hashrate || 0), 0);

      // Calculate Dogecoin hashrate (comes in MH/s, keep as MH/s for now)
      const dogecoinTotalMH = onlinedDogecoinMiners.reduce((sum, miner) => sum + (miner.hashrate || 0), 0);

      console.log(`📊 Pool hashrates - SHA256: ${sha256TotalMH.toFixed(3)} MH/s,DOGECOIN: ${dogecoinTotalMH.toFixed(3)}`);

      return {
        sha256: {
          totalHashrate: sha256TotalMH, // MH/s
          onlineMinersCount: onlineSha256Miners.length,
          totalMinersCount: sha256Miners.length,
          unit: 'MH/s'
        },
        dogecoin: {
          totalHashrate: dogecoinTotalMH, // MH/s
          onlineMinersCount: onlinedDogecoinMiners.length,
          totalMinersCount: dogecoinMiners.length,
          unit: 'MH/s'
        }
      };

    } catch (error) {
      console.error('❌ Failed to calculate pool hashrates:', error);
      return {

        sha256: {
          totalHashrate: 0,
          onlineMinersCount: 0,
          totalMinersCount: 0,
          unit: 'MH/s'
        },
        dogecoin: {
          totalHashrate: 0,
          onlineMinersCount: 0,
          totalMinersCount: 0,
          unit: 'MH/s'
        }
      };
    }
  }
};

export default miningServices;